import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth";
import { useLang } from "../i18n";

export default function Login() {
  const { login } = useAuth();
  const { L } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const oauthError = location.state?.error || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password);
      navigate(data?.client?.role === "team" ? "/team" : "/portal");
    } catch (err) {
      setError(
        err.message === "Invalid credentials"
          ? L({ en: "Incorrect email or password.", it: "Email o password non corretti." })
          : L({ en: "Login failed. Please try again.", it: "Accesso non riuscito. Riprova." })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-104px)] lg:grid-cols-2" data-testid="login-page">
      <div className="hidden flex-col justify-between bg-[#0E3B33] px-14 py-20 lg:flex">
        <div>
          <div className="mb-8 h-px w-14 bg-[#C9A84C]" />
          <h1 className="font-display text-4xl leading-tight text-[#FBF9F3] sm:text-5xl">
            {L({ en: <>Your projects,<br />always at hand.</>, it: <>I tuoi progetti,<br />sempre a portata di mano.</> })}
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#FBF9F3]/70">
            {L({
              en: "Track every commission through the atelier, share documents, and talk directly with the DA Creations team.",
              it: "Segui ogni commissione attraverso l'atelier, condividi documenti e parla direttamente con il team DA Creations.",
            })}
          </p>
        </div>
        <p className="text-xs text-[#FBF9F3]/40">
          {L({
            en: "Access is by invitation — accounts are created by the atelier when your project begins.",
            it: "L'accesso è su invito — gli account vengono creati dall'atelier all'inizio del progetto.",
          })}
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <p className="mono-tech mb-4 text-[#A8853B]">{L({ en: "Client Area", it: "Area Clienti" })}</p>
          <h2 className="font-display text-3xl text-[#0E3B33] sm:text-4xl">{L({ en: "Welcome back.", it: "Bentornato." })}</h2>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="mono-tech mb-2 block text-[#4A5248]">Email</label>
              <input required type="email" data-testid="login-email-input" className="forge-field" placeholder="you@yourbrand.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="mono-tech mb-2 block text-[#4A5248]">Password</label>
              <input required type="password" data-testid="login-password-input" className="forge-field" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <p className="text-sm text-red-700" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-btn" className="ember-btn w-full justify-center disabled:opacity-60">
              {busy ? L({ en: "Signing in…", it: "Accesso…" }) : L({ en: "Sign in", it: "Accedi" })}
            </button>
          </form>
          <div className="mt-6 flex items-center gap-4" data-testid="login-divider">
            <span className="h-px flex-1 bg-[#0E3B33]/10" />
            <span className="mono-tech text-[#8A8F82]">{L({ en: "or", it: "oppure" })}</span>
            <span className="h-px flex-1 bg-[#0E3B33]/10" />
          </div>
          <button type="button" onClick={googleSignIn} data-testid="google-signin-btn" className="ghost-btn mt-5 w-full justify-center">
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            {L({ en: "Continue with Google", it: "Continua con Google" })}
          </button>
          {oauthError && (
            <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-800" data-testid="oauth-error">
              {oauthError === "no_account"
                ? L({
                    en: "No client account exists for that Google email. Accounts are created by the atelier — send us a brief first.",
                    it: "Nessun account cliente associato a questa email Google. Gli account vengono creati dall'atelier — inviaci prima un brief.",
                  })
                : L({ en: "Google sign-in failed. Please try again.", it: "Accesso con Google non riuscito. Riprova." })}
            </p>
          )}
          <p className="mt-4 text-center text-xs text-[#8A8F82]">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="transition-colors hover:text-[#0E3B33]">
              {L({ en: "Forgot your password?", it: "Password dimenticata?" })}
            </Link>
          </p>
          <p className="mt-3 text-center text-xs leading-relaxed text-[#8A8F82]">
            {L({ en: "No account yet?", it: "Non hai ancora un account?" })}{" "}
            <Link to="/contact" data-testid="login-contact-link" className="font-medium text-[#A8853B] hover:text-[#0E3B33]">
              {L({ en: "Start a project with us", it: "Inizia un progetto con noi" })}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
